'use client';

import { MouseEvent, useCallback } from 'react';

import clsx from 'clsx';

import { Check, Pause, Play } from '../Icons';

import styles from './VoiceItem.module.scss';

type VoiceItemProps = {
  /**
   * The voice label.
   */
  label: React.ReactNode;

  accentLabel?: string;

  /**
   * The voice image alt.
   */
  alt: string;
  /**
   * The voice image.
   */
  image: string;
  /**
   * The voice id.
   */
  id: string;
  /**
   * The voice selected state.
   */
  selected?: boolean;
  /**
   * The additional class names for the container div.
   */
  className?: string;
  /**
   * The callback to handle the click.
   */
  onClick: (id: string) => void;
  /**
   * The callback to handle the play click.
   */
  onPlayClick: (id: string) => void;

  /**
   * The voice playing state.
   */
  isPlaying?: boolean;
};

export const VoiceItem = ({
  label,
  accentLabel,
  alt,
  image,
  id,
  selected,
  className,
  onClick,
  onPlayClick,
  isPlaying,
}: VoiceItemProps) => {
  const clickHandler = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onClick(id);
    },
    [id, onClick],
  );

  const playClickHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onPlayClick(id);
    },
    [id, onPlayClick],
  );

  return (
    <div className={styles.container}>
      <button
        className={clsx(styles.wrapper, selected && styles.selected, className)}
        onClick={clickHandler}
        type="button"
      >
        <div className={styles['avatar-wrapper']} role="checkbox" aria-checked={selected} onClick={clickHandler}>
          <img
            src={image}
            alt={alt}
            className={styles.avatar}
            draggable={false}
            onError={(e) => {
              e.currentTarget.src = '/ai-profile-pictures/parrot.png';
            }}
          />

          <div className={styles['label-wrapper']}>
            {accentLabel && (
              <div className={styles['accent']} title={accentLabel}>
                <h3>{accentLabel}</h3>
              </div>
            )}

            <div className={styles.label}>
              <h2>{label}</h2>
            </div>
          </div>
        </div>
      </button>

      <button className={styles.play} onClick={playClickHandler} type="button">
        {isPlaying ? <Pause size={11} aria-label={'Pause'} /> : <Play size={16} aria-label="Play" />}
      </button>
    </div>
  );
};
